import React from "react";
import BehaviourBarGraph from "../BehaviourBarGraph.jsx";
import {
  formatNumber,
  formatPercentage,
} from "@signolteam/shared/units-of-measure";

let defaultMetadata = {
  obl_min_me_load_percent: 40,
  obl_threshold_kg_h: 30,
  obl_boiler_fuel_actual_kg_h: 27,
  obls_threshold_kg_h: 30,
  obls_boiler_fuel_actual_kg_h: 27,
  obls_boiler_fuel_mgo_corrected_kg_h: null,
  obls_boiler_fuel_in_port_threshold_kg_h: null,
  oblp_threshold_kg_h: 30,
  oblp_boiler_fuel_actual_kg_h: 27,
  oblp_boiler_fuel_mgo_corrected_kg_h: null,
  oblp_boiler_fuel_in_port_threshold_kg_h: null,
  report_category: "IN_PORT_REPORT",
};
export const behaviourMetadataFields = Object.keys(defaultMetadata);
export default function graph({
  bvrColour,
  metadata = defaultMetadata,
  missingDataMsg,
  customerCode,
  isExample,
  acronym,
}) {
  const minMainEngineLoadPercent = Math.ceil(
    Number(metadata["obl_min_me_load_percent"] || 40),
  );
  let atSea = metadata.report_category
    ? metadata.report_category.toUpperCase() === "AT_SEA_REPORT"
    : //Fallback using report_type
      metadata.report_type
      ? ["EOSP", "ARRIVAL"].includes(metadata.report_type.toUpperCase()) ||
        metadata.report_type.toUpperCase().includes("SEA")
      : false;

  const internalAcronym = acronym.split(":").pop(); // In case of group:behaviour format, take the behaviour code for graph data keys
  if (isExample && acronym.split(":")[1]) {
    atSea = internalAcronym.toLowerCase().startsWith("obls");
  }

  const prefix = internalAcronym
    ? internalAcronym.toLowerCase()
    : acronym.toLowerCase();
  const threshold = Number(metadata[`${prefix}_threshold_kg_h`]);
  const actual = Number(
    metadata[`${prefix}_boiler_fuel_mgo_corrected_kg_h`] ??
      metadata[`${prefix}_boiler_fuel_actual_kg_h`],
  );

  if (isNaN(actual) || isNaN(threshold)) {
    return (
      <div className="relative text-center my-6 mx-auto text-2xl fill-current">
        {missingDataMsg}
      </div>
    );
  }

  const minMELoadFormatted = formatPercentage(
    "0-100",
    minMainEngineLoadPercent,
    undefined,
    { precisionOverride: 0 },
  ).formattedValue;

  let yAxisLabel = atSea
    ? "At sea auxiliary boiler consumption"
    : "In port auxiliary boiler consumption";
  if (customerCode === "CHR") {
    yAxisLabel = atSea
      ? "At sea boiler consumption (excluding cargo heating and cleaning)"
      : "In port boiler consumption (excluding cargo heating and cleaning)";
  }
  const data = [
    {
      name: yAxisLabel,
      mobileName: "Label",
      beforeThreshold: actual > threshold ? threshold : actual,
      afterThreshold: actual > threshold ? actual - threshold : 0,
      underThresholdColour:
        actual > threshold ? bvrColour.codes["200"] : bvrColour.codes["500"],
      overThresholdColour: bvrColour.codes["900"],
    },
  ];

  const domainMin = 0;
  const margin = 0.2 * actual;
  const domainMax =
    actual < threshold
      ? Math.ceil((threshold + margin) / 10) * 10
      : Math.ceil((actual + margin) / 10) * 10;
  const domain = [domainMin, domainMax];

  const mobileMargin = actual < threshold ? 0.1 * threshold : 0.1 * actual;
  const mobileDomainMax =
    actual < threshold
      ? Math.ceil(threshold + mobileMargin)
      : Math.ceil(actual + mobileMargin);
  const mobileDomain = [domainMin, mobileDomainMax];

  const thresholdLineColour = bvrColour.codes["900"];

  const numOfTicks =
    actual > threshold ? Math.floor(actual / 10) : Math.floor(threshold / 10);

  const ticks = [];
  // add 1 for 0 and 1 for the extra margin
  for (let i = 0; i < numOfTicks + 2; i++) {
    ticks.push(10 * i);
  }

  const unitOfMeasure = "MT/hr";

  let graphProps = {};

  if (Number(threshold) === 0 && Number(actual) === 0 && atSea) {
    // at sea success
    return (
      <div className="w-full flex flex-row justify-center items-center my-6">
        <div className={`bg-${bvrColour.name}-100 p-6`}>
          {`This OBL success is because the ME load at sea was > ${minMELoadFormatted} and you and your crew did not engage the auxiliary boiler.`}
        </div>
      </div>
    );
  } else {
    // normal success/fail
    graphProps = {
      data,
      threshold,
      thresholdLabel: `${
        !isExample
          ? `Ship-specific threshold (${formatNumber(threshold / 1000, {
              precision: 3,
              unit: unitOfMeasure,
              options: { boundaryValues: [actual / 1000] },
            })})`
          : "Ship-specific threshold"
      }`,
      mobileDomain,
      domain,
      refLineColour: thresholdLineColour,
      success: actual < threshold,
      unitOfMeasure,
      axisLabel: atSea
        ? `At sea auxiliary boiler consumption (MT/hr) when ME load > ${minMELoadFormatted}`
        : "Average auxiliary boiler consumption (MT/ hr)",
      hideYLine: true,
      hideNum: true,
      barLabelFormatter: (value) =>
        `${formatNumber(value / 1000, { precision: 3, unit: "MT/hr", options: { boundaryValues: [0, threshold] } })}`,
      ticks,
      hideYLabelOnMobile: true,
      hideXLineOnMobile: false,
    };
  }

  return <BehaviourBarGraph {...graphProps} isExample={isExample} />;
}
